import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import Input from '../../common/Input';
import Select from '../../common/Select';
import Textarea from '../../common/Textarea';
import Button from '../../common/Button';
import { validateRequired } from '../../../utils/validators';
import customerService from '../../../services/customerService';
import api from '../../../services/api';
import { API_ENDPOINTS } from '../../../constants/config';
import { IATA_CODES } from '../../../constants/iataCodes';
import branchService from '../../../services/branchAddressService';

const AddSiteModal = ({ isOpen, onClose, onSubmit }) => {
  const initialState = {
    company_id: '',
    name: '',
    location: '',
    address: '',
    escalation_matrix_id: '',
    branch_address_id: ''
  };

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  const [companies, setCompanies] = useState([]);
  const [matrices, setMatrices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen]);

  const loadInitialData = async () => {
    setLoadingInitial(true);
    try {
      const [compRes, matRes, branchRes] = await Promise.all([
        customerService.getCustomerCompanies(),
        api.get(API_ENDPOINTS.ESCALATION_MATRICES),
        branchService.getAll()
      ]);
      
      if (compRes.success) setCompanies(compRes.data);
      setMatrices(matRes.data || []);
      if (branchRes.success) setBranches(branchRes.data);
    } catch (error) {
      console.error("Failed to load initial site data", error);
    } finally {
      setLoadingInitial(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!validateRequired(formData.company_id)) newErrors.company_id = 'Company is required';
    if (!validateRequired(formData.name)) newErrors.name = 'Site name is required';
    if (!validateRequired(formData.location)) newErrors.location = 'Location is required';
    if (!validateRequired(formData.address)) newErrors.address = 'Address is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        company_id: parseInt(formData.company_id),
        escalation_matrix_id: formData.escalation_matrix_id ? parseInt(formData.escalation_matrix_id) : null,
        branch_address_id: formData.branch_address_id ? parseInt(formData.branch_address_id) : null
      };
      await onSubmit(payload);
      setFormData(initialState);
      onClose();
    } catch (error) {
      console.error('Error creating Site:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData(initialState);
      setErrors({});
      onClose();
    }
  };

  const companyOptions = [
    { value: '', label: '-- Select Company --' },
    ...companies.map(c => ({ value: c.id.toString(), label: c.company_name }))
  ];

  const locationOptions = [
    ...IATA_CODES.map(code => ({ value: code, label: code }))
  ];

  const matrixOptions = [
    ...matrices.map(m => ({ value: m.id.toString(), label: m.name }))
  ];

  const branchOptions = [
    ...branches.map(b => ({ value: b.id.toString(), label: b.is_default ? `${b.name} (Default)` : b.name }))
  ];

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '16px'
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Adding...' : 'Add Site'}
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New Site" size="medium" footer={footer}>
      <form onSubmit={handleSubmit}>
        <div style={gridStyle}>
          <Select
            label="Company"
            name="company_id"
            value={formData.company_id}
            onChange={handleChange}
            options={companyOptions}
            error={errors.company_id}
            disabled={loadingInitial}
            required
          />
          <Select
            label="Location (IATA)"
            name="location"
            value={formData.location}
            onChange={handleChange}
            options={locationOptions}
            error={errors.location}
            required
          />
        </div>

        <div style={gridStyle}>
          <Input 
            label="Site Name" 
            name="name" 
            placeholder="e.g., Koramangala HQ" 
            value={formData.name} 
            onChange={handleChange} 
            error={errors.name} 
            required 
          />
          <Select
            label="Escalation Matrix"
            name="escalation_matrix_id"
            value={formData.escalation_matrix_id}
            onChange={handleChange}
            options={matrixOptions}
            disabled={loadingInitial}
          />
        </div>
        <div style={gridStyle}>
          <Select
            label="Branch Address (Letterhead)"
            name="branch_address_id"
            value={formData.branch_address_id}
            onChange={handleChange}
            options={branchOptions}
            disabled={loadingInitial}
          />
        </div>

        <div style={{ marginTop: '16px' }}>
           <Textarea
             label="Detailed Address"
             name="address"
             placeholder="Enter full address of the site..."
             value={formData.address}
             onChange={handleChange}
             error={errors.address}
             required
             rows={3}
           />
        </div>

        <div style={{
          padding: '12px 16px', background: 'var(--surface-light)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-secondary)', marginTop: '16px'
        }}>
          💡 Note: Mappings for SPOCs, Team Leads, and AMC Contracts will be done from their respective management panels.
        </div>
      </form>
    </Modal>
  );
};

export default AddSiteModal;